import { Stack } from '@startuptools/ui/Stack';
import { Card } from '@startuptools/ui/Card';
import {
  GqlArticlesOfAssoc,
  GqlAuthorization,
  GqlAuthorizationGroup,
  GqlMeetingConcern,
  GqlShareIssue,
  GqlWarrantProgram,
} from '../../../../graphql/base-types.graphql';
import { Trans } from '@lingui/react';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Radio } from '@startuptools/ui/Radio';
import { useMcSetApprovedMutation } from './MeetingConcern.graphql';
import { BoardMeetingDocument } from '../../../board-meetings/BoardMeeting.graphql';
import { ShareholdersMeetingDocument } from '../../../shareholders-meetings/ShareholdersMeeting.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { ReactNode } from 'react';

export type AiMcProps = Pick<GqlMeetingConcern, 'id' | 'approved' | 'appendixNumber'> & {
  articlesOfAssoc?: Pick<GqlArticlesOfAssoc, 'id' | 'createdAt'> | null;
  authorizationGroup?:
    | (Pick<GqlAuthorizationGroup, 'id'> & { authorizations: Pick<GqlAuthorization, 'id' | 'kind'>[] })
    | null;
  shareIssue?: Pick<GqlShareIssue, 'id' | 'description'> | null;
  warrantProgram?: Pick<GqlWarrantProgram, 'id' | 'name' | 'description' | 'warrantKind'> | null;
};

export const AiMeetingConcern = ({
  details,
  meetingConcerns,
}: {
  details: (mc: AiMcProps) => ReactNode;
  meetingConcerns: AiMcProps[];
}) => {
  const companyId = useCompanyId();

  // eslint-disable-next-line sut/loading-mutation
  const [doSetApproved] = useMcSetApprovedMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument],
  });

  const handleChange = async (input: { id: string; approved: boolean }) => {
    await doSetApproved({
      variables: { companyId, input },
      optimisticResponse: {
        mcSetApproved: {
          id: input.id,
          __typename: 'MeetingConcern',
          approved: input.approved,
        },
      },
    });
  };

  return (
    <Stack gap={1}>
      {meetingConcerns.map(mc => (
        <Card key={mc.id}>
          <Stack direction="row" gap={1} justifyContent="space-between">
            <>{details(mc)}</>
            <RadioGroup
              value={mc.approved}
              onChange={e => handleChange({ id: mc.id, approved: e.target.value === 'true' })}
              orientation="horizontal"
            >
              <Radio value label={<Trans id="Approve" />} />
              <Radio value={false} label={<Trans id="Reject" />} />
            </RadioGroup>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
};
